import { useEffect, useState } from "react";
import QuestionBox from "../../ui/QuestionBox/QuestionBox";
import RadioButton from "../../ui/RadioButton/RadioButton";
import classes from "../Infrastructure/Infrastructure.module.css";
import QuestionCard from "../../ui/QuestionCard/QuestionCard";
import CommonFooter from "../../CommonFooter/CommonFooter";
import axios from "axios";
import { useAuth } from "../../../context/AuthContext";
import { env } from "../../../constants/env";
import { Bounce, toast } from "react-toastify";
import InputField from "../../ui/InputField/InputField";
import { useParams } from "react-router-dom";
import CommonButton from "../../ui/CommonButton/CommonButton";

const initialState = {
  receivesReferralSpecimens: "",
  testsReferredToOtherLabs: ["", ""],
  referralDistanceKm: "",
  referralTurnAroundTime: [
    {
      serial: "",
      testName: "",
      tat: "",
    },
  ],

  referralComment: "",
};

const SpecimenReferral = () => {
  const [currentSet, setCurrentSet] = useState(0);
  const [answers, setAnswers] = useState(["", ""]);
  const { user } = useAuth();

  const { labId: paramLabId } = useParams();
  const labId = paramLabId || user?.labId;

  const userRole = user?.role;

  const [formData, setFormData] = useState(initialState);
  const [ownerLabId, setOwnerLabId] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isfetching, setIsFetching] = useState(false);

  const isReadOnly = userRole !== "LABORATORY" || !editMode;

  const sectionName = "specimen-referral"; // formType

  useEffect(() => {
    if (labId) {
      fetchQuestionnaire();
    }
  }, [labId]);

  const fetchQuestionnaire = async () => {
    try {
      setIsFetching(true);
      const res = await axios.get(
        `${env.kataApiUrl}/api/questionnaire/${labId}/${sectionName}`
      );

      if (res.data) {
        if (res.data.answers) {
          setFormData(res.data.answers);
          setEditMode(false); // ✅ answers exist → read-only by default
        } else {
          setFormData(initialState);
          setEditMode(true); // ✅ no answers → open in edit mode
        }
        setOwnerLabId(res.data.labId);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        console.log("No existing questionnaire found for this lab.");
        setFormData(initialState);
        setEditMode(true); // ✅ new lab → start in edit mode
      } else {
        console.error("Error fetching questionnaire:", error);
        toast.error("Failed to load saved questionnaire");
      }
    } finally {
      setIsFetching(false);
    }
  };

  // --- pass disabled to inputs ---
  const handleChange = (field, value) => {
    if (isReadOnly) return; // block edits
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNestedChange = (section, key, value) => {
    if (isReadOnly) return;
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value,
      },
    }));
  };

  const handleNext = () => {
    if (currentSet < questionSets.length - 1) setCurrentSet(currentSet + 1);
  };

  const handlePrevious = () => {
    if (currentSet > 0) setCurrentSet(currentSet - 1);
  };

  const handleDone = async () => {
    try {
      setLoading(true);
      const payload = {
        labId,
        formType: sectionName,
        answers: formData,
      };

      const res = await axios.post(
        `${env.kataApiUrl}/api/questionnaire/${sectionName}`,
        payload
      );

      toast.success("Submitted successfully!");

      fetchQuestionnaire();
      setCurrentSet(0);
      setEditMode(false); // exit edit mode

      console.log(res.data);
    } catch (error) {
      console.error(error);
      toast.error("Submission failed");
    } finally {
      setLoading(false);
    }
  };

  const questionSets = [
    {
      subtitle: "Specimen referral to other laboratories",
      content: (
        <div className="d-flex flex-column gap-4">
          <QuestionBox
            number={1}
            question="Does the laboratory receive referral specimens from other laboratories"
            disabled={isReadOnly}
          >
            <div className="d-flex gap-2 mb-4">
              <RadioButton
                label="Yes"
                name="receivesCommunication"
                checked={formData.receivesCommunication === "Yes"}
                onChange={() => handleChange("receivesCommunication", "Yes")}
                disabled={isReadOnly}
              />
              <RadioButton
                label="No"
                name="receivesCommunication"
                checked={formData.receivesCommunication === "No"}
                onChange={() => handleChange("receivesCommunication", "No")}
                disabled={isReadOnly}
              />
            </div>
          </QuestionBox>
          <QuestionBox
            number={2}
            question="List the tests referred to other Laboratories"
            singleInput
            answers={formData.testsReferredToOtherLabs}
            onAnswersChange={(newAnswers) =>
              handleChange("testsReferredToOtherLabs", newAnswers)
            }
            disabled={isReadOnly}
          />

          <QuestionBox
            number={3}
            question="Approximation of the distance to the referral Laboratory (In Kilometers)"
            disabled={isReadOnly}
          >
            <div
              className={`${classes.customInput} d-flex align-items-center gap-2`}
            >
              <div className={classes.prepend}>KM</div>
              <input
                type="text"
                placeholder="Enter distance"
                onChange={(e) =>
                  handleChange("referralDistanceKm", e.target.value)
                }
                value={formData.referralDistanceKm}
                disabled={isReadOnly}
              />
            </div>
          </QuestionBox>

          <QuestionBox
            number={4}
            question="Turn Around Time(TAT) for the referred tests"
            groupedInput
            answers={formData.referralTurnAroundTime}
            onAnswersChange={(updated) =>
              setFormData((prev) => ({
                ...prev,
                referralTurnAroundTime: updated,
              }))
            }
            multilineInput
            placeholder="Add comment"
            value={formData.referralComment}
            onChange={(e) => handleChange("referralComment", e.target.value)}
            disabled={isReadOnly}
          ></QuestionBox>
        </div>
      ),
    },
  ];

  const { subtitle, content } = questionSets[currentSet];

  if (isfetching) {
    return <div className={classes.loading}>Loading...</div>;
  }

  return (
     <div
      className={`${classes.wrapper} ${
        paramLabId ? classes.fromParams : ""
      } ${isReadOnly ? classes.readOnly : ""}`}
    >
      <div className="d-flex justify-content-between align-items-center">
        <h2>Specimen referral</h2>
        {userRole === "LABORATORY" && !editMode && (
          <CommonButton
            className={classes.editButton}
            onClick={() => setEditMode(true)}
            title="Edit"
          />
        )}
      </div>
      {(!editMode && userRole !== "MINISTRY") ? (
        <div className={classes.infoText}>
          ✅ Your specimen referral has been submitted.
        </div>
      ) : (
        content
      )}
      {!isReadOnly && (
        <CommonFooter
          handlePrevious={handlePrevious}
          handleNext={handleNext}
          handleDone={handleDone}
          cancelText="Cancel"
          handleCancel={() => {
            setEditMode(false);
            fetchQuestionnaire();
            setCurrentSet(0);
          }}
          questionSets={questionSets}
          currentSet={currentSet}
          doneText={loading ? "Submitting..." : "Submit"}
          disabled={loading}
        />
      )}
    </div>
  );
};

export default SpecimenReferral;
